/**
 * SME Dashboard — Clean, Journey-Centric Control Panel
 *
 * Design principles:
 * - One screen = one purpose (re-entry and progress overview)
 * - Every section synced from JourneyContext (live data)
 * - No jargon, no empty widgets, no broken states
 * - One clear next action at all times
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import ActivationBanner from "@/components/ActivationBanner";
import CRSPanel from "@/components/CRSPanel";
import AuditProvenancePanel from "@/components/AuditProvenancePanel";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  ArrowRight, CheckCircle2, TrendingDown, DollarSign, Zap,
  Shield, Edit3, Target, FileText, ChevronRight, AlertCircle,
  Check
} from "lucide-react";

// ── Unlock Financing — Dashboard Summary ──────────────────────────────────
function UnlockFinancingDashboard({ navigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/sme/workspace/unlock-financing")
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const GATE_CONFIG = {
    full:        { label: "Finance Ready",            labelStyle: "text-emerald-700 bg-emerald-100", bar: "bg-emerald-500" },
    preliminary: { label: "Ready for Financing Review", labelStyle: "text-teal-700 bg-teal-100",    bar: "bg-teal-500"   },
    blocked:     { label: "Not Yet Ready",             labelStyle: "text-slate-600 bg-slate-100",    bar: "bg-slate-300"   },
  };
  const gc = GATE_CONFIG[data.gate] || GATE_CONFIG.blocked;
  const pct = Math.round((data.completed_count / data.total_count) * 100);
  const firstIncomplete = data.items?.find(i => !i.completed);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="dashboard-unlock-financing">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Unlock Financing</p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${gc.labelStyle}`}>{gc.label}</span>
        </div>
        <button onClick={() => navigate("/sme/journey")}
          className="text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
          View checklist <ArrowRight size={11} />
        </button>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate-400 mb-1">
          <span>{data.completed_count}/{data.total_count} requirements</span>
          <span>Target: {data.target_score}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${gc.bar}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Next action */}
      {firstIncomplete && (
        <div className="bg-teal-50 border border-teal-100 rounded-xl px-3 py-2.5 flex items-start gap-2 cursor-pointer hover:bg-teal-100 transition-colors"
          onClick={() => navigate("/sme/journey")}>
          <ArrowRight size={12} className="text-teal-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-teal-800">{firstIncomplete.label}</p>
            <p className="text-xs text-teal-600">Step {firstIncomplete.step_label} · +{firstIncomplete.pts_gain}% confidence</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step progress indicator ────────────────────────────────────────────────
const STEPS = [
  { id: "profile",      label: "Profile"      },
  { id: "data",         label: "Data"         },
  { id: "impact",       label: "Impact"       },
  { id: "opportunities",label: "Opportunities"},
  { id: "financing",    label: "Financing"    },
];

function JourneyBanner({ journey, navigate }) {
  const fp   = journey?.footprint;
  const act  = journey?.actions;
  const opp  = journey?.opportunity;
  const hasData = !!fp;
  const hasAct  = !!(act?.action_count > 0);

  const stepState = (id) => {
    // If journey is submitted to FI → everything is done
    if (journey?.conversion?.submission_status === "submitted") return "done";
    // Normal progression
    if (id === "profile")       return "done";
    if (id === "data")          return hasData ? "done" : "active";
    if (id === "impact")        return hasData ? "done" : "upcoming";
    if (id === "opportunities") return hasData ? "done" : "upcoming";
    if (id === "financing")     return hasData ? "done" : "upcoming";
    return "upcoming";
  };

  const allDone  = hasData;
  const nextStep = allDone ? null : STEPS.find(s => stepState(s.id) === "active");

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-6 py-5" data-testid="journey-banner">
      {/* Step dots */}
      <div className="flex items-center gap-0 mb-4">
        {STEPS.map((s, i) => {
          const state = stepState(s.id);
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <div className={`flex-1 h-0.5 mx-1 ${state === "done" || (STEPS[i-1] && stepState(STEPS[i-1].id) === "done") ? "bg-teal-400" : "bg-slate-100"}`} />
              )}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${
                  state === "done"     ? "bg-teal-500 border-teal-500" :
                  state === "active"   ? "bg-white border-teal-500" :
                                         "bg-slate-50 border-slate-200"
                }`}>
                  {state === "done"
                    ? <CheckCircle2 size={14} className="text-white" />
                    : <span className={`text-xs font-bold ${state === "active" ? "text-teal-600" : "text-slate-300"}`}>{i+1}</span>
                  }
                </div>
                <p className={`text-xs mt-0.5 font-semibold hidden sm:block ${
                  state === "done" ? "text-teal-600" :
                  state === "active" ? "text-slate-700" : "text-slate-300"
                }`}>{s.label}</p>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-700">
            {allDone ? "Assessment Complete — Ready for Financing Review" : nextStep ? `Next: ${nextStep.label}` : "Journey complete"}
          </p>
          {allDone && <p className="text-xs text-teal-600 mt-0.5">All steps verified · CRS computed · Report available</p>}
        </div>
        <button onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 transition-colors"
          data-testid="view-journey-btn">
          View Journey <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ── 4 KPI Cards ────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, iconBg, iconColor, value, unit, label, sub, tooltip }) {
  return (
    <MetricCard
      label={label}
      value={value ?? "—"}
      unit={unit}
      sub={sub}
      icon={Icon}
      iconBg={iconBg}
      className={tooltip ? "cursor-help" : ""}
    />
  );
}

// ── Climate Snapshot ───────────────────────────────────────────────────────
function ClimateSnapshot({ journey, navigate }) {
  const fp  = journey?.footprint;
  const wm  = journey?.what_this_means;
  if (!fp) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center min-h-[200px] text-center">
        <TrendingDown size={28} className="text-slate-200 mb-3" />
        <p className="text-sm font-semibold text-slate-500 mb-1">No emissions data yet</p>
        <p className="text-xs text-slate-400 mb-4">Enter your data to see your emissions profile</p>
        <button onClick={() => navigate("/sme-journey")}
          className="px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
          Start Journey
        </button>
      </div>
    );
  }

  const cats = (fp.by_category || [])
    .filter(c => c.scope !== "intensity_only" && c.kgco2e > 0)
    .sort((a, b) => b.kgco2e - a.kgco2e)
    .slice(0, 3);
  const total = fp.total_tco2e || 0;

  const friendly = {
    "Fleet Diesel (Scope 1)":     "Fleet vehicles",
    "Generator Diesel (Scope 1)": "Generator fuel",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="climate-snapshot">
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Annual Carbon Footprint</p>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-slate-900 tabular-nums leading-none">{total}</span>
            <span className="text-base text-slate-400 mb-1">tCO₂e/yr</span>
          </div>
        </div>
        <button onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-1 text-[11px] text-teal-600 font-semibold hover:underline">
          View details <ChevronRight size={12} />
        </button>
      </div>

      {/* Bars */}
      <div className="space-y-2.5 mb-4">
        {cats.map(cat => {
          const pct = Math.round(cat.kgco2e / (total * 1000) * 100);
          const label = friendly[cat.label] || cat.label.replace(/\s*\(.*\)/, "");
          return (
            <div key={cat.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold text-slate-900">{Math.round(cat.kgco2e/1000*10)/10}t · {pct}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-teal-500 transition-all duration-700"
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Key insight */}
      {wm?.biggest_source && (
        <div className="bg-[#0F172A] rounded-xl px-4 py-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-0.5">Key Insight</p>
          <p className="text-xs text-white">{wm.biggest_source}</p>
          {wm.so_what && <p className="text-xs text-teal-400 mt-1">{wm.so_what}</p>}
        </div>
      )}

      {/* Industry benchmark — elevated card */}
      {fp.industry_benchmark && (
        <div className="bg-slate-900 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">vs Industry ({fp.industry_benchmark.sector_label})</p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-white">{total}</span>
              <span className="text-xs text-slate-400">tCO₂e/yr</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Industry range: <span className="text-slate-300 font-semibold">{fp.industry_benchmark.avg_range}</span>
            </p>
          </div>
          <span className={`text-xs font-bold px-2.5 py-1.5 rounded-lg flex-shrink-0 ${
            fp.industry_benchmark.vs_average === "more_efficient"
              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
              : fp.industry_benchmark.vs_average === "at_average"
              ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
              : "bg-red-500/20 text-red-400 border border-red-500/30"
          }`}>
            {fp.industry_benchmark.label}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Top Opportunity Card ────────────────────────────────────────────────────
function TopOpportunityCard({ journey, navigate }) {
  const actions = journey?.actions?.actions || [];
  const opp = journey?.opportunity;
  const top = actions[0];

  if (!top) {
    return (
      <SectionCard>

        <Target size={24} className="text-slate-200 mb-2" />
        <p className="text-xs text-slate-400">Opportunities unlock after data entry</p>
      
      </SectionCard>
    );
  }

  const LABELS = { value_chain: "Highest Value", transport: "High Impact", renewable_energy: "High Impact", process: "Good Impact" };
  const COLORS = { value_chain: "bg-emerald-100 text-emerald-700", transport: "bg-teal-100 text-teal-700", renewable_energy: "bg-blue-100 text-blue-700", process: "bg-slate-100 text-slate-600" };
  const shortDesc = top.description?.split(".")[0].replace(/\([^)]*\)/g, "").trim();

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="top-opportunity">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Top Opportunity</p>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${COLORS[top.action_type] || "bg-slate-100 text-slate-500"}`}>
          {LABELS[top.action_type] || "Impact"}
        </span>
      </div>
      <p className="text-sm font-semibold text-slate-900 mb-3 leading-snug">{shortDesc || top.action_type?.replace(/_/g," ")}</p>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-50 rounded-xl px-3 py-2">
          <p className="text-xs text-slate-400">Potential reduction</p>
          <p className="text-base font-bold text-teal-600">{top.co2_reduction}t CO₂e/yr</p>
        </div>
        {opp?.vcm_value_low > 0 && (
          <div className="bg-slate-50 rounded-xl px-3 py-2">
            <p className="text-xs text-slate-400">Carbon value (indicative)</p>
            <p className="text-base font-bold text-emerald-600">
              {opp.vcm_value_low >= 1000
                ? `$${(opp.vcm_value_low/1000).toFixed(0)}k–$${(opp.vcm_value_high/1000).toFixed(0)}k/yr`
                : `$${Math.round(opp.vcm_value_low)}–$${Math.round(opp.vcm_value_high)}/yr`}
            </p>
          </div>
        )}
      </div>
      <button onClick={() => navigate("/sme-journey")}
        className="text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
        See all opportunities <ChevronRight size={12} />
      </button>
    </div>
  );
}

// ── Next Action Card ───────────────────────────────────────────────────────
function NextActionCard({ journey, navigate }) {
  const fp   = journey?.footprint;
  const act  = journey?.actions;
  const hasData = !!fp;
  const hasAct  = !!(act?.action_count > 0);

  let action;
  if (!hasData) {
    action = {
      label:  "Enter Your Data",
      desc:   "Your journey starts with measuring what you emit. Takes about 5 minutes.",
      cta:    "Start Now",
      icon:   Edit3,
      path:   "/sme-journey",
      color:  "bg-teal-600 text-white",
    };
  } else if (!hasAct) {
    action = {
      label:  "Review Your Opportunities",
      desc:   "Your emissions are calculated. See what you can do and how much it's worth.",
      cta:    "View Opportunities",
      icon:   Target,
      path:   "/sme-journey",
      color:  "bg-teal-600 text-white",
    };
  } else {
    action = {
      label:  "Request Financing",
      desc:   "Your data and opportunities are ready. Request funding from a finance partner.",
      cta:    "Request Financing",
      icon:   DollarSign,
      path:   "/sme-journey",
      color:  "bg-[#0F172A] text-white",
    };
  }

  const ActionIcon = action.icon;

  return (
    <div className="bg-[#0F172A] rounded-xl p-6 text-white" data-testid="next-action-card">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Next Action</p>
      <p className="text-base font-bold mb-1">{action.label}</p>
      <p className="text-xs text-slate-400 mb-4">{action.desc}</p>
      <button onClick={() => navigate(action.path)}
        className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-500 transition-colors"
        data-testid="next-action-btn">
        <ActionIcon size={13} /> {action.cta}
      </button>
    </div>
  );
}

// ── Empty State (no data at all) ───────────────────────────────────────────
function EmptyJourneyDashboard({ user, org, navigate }) {
  const STEPS_EMPTY = [
    { n: 1, label: "Set Up Profile",        done: true  },
    { n: 2, label: "Enter Your Data",       done: false },
    { n: 3, label: "See Your Impact",       done: false },
    { n: 4, label: "Find Opportunities",    done: false },
    { n: 5, label: "Request Financing",     done: false },
  ];

  return (
    <div className="space-y-5">
      {/* Greeting */}
      <div>
        
      <PageHeader
        label="Unlock Financing"
        title="Welcome to DeCarbonIQ"
      />
      </div>

      {/* Start card */}
      <div className="bg-[#0F172A] rounded-xl p-7">
        <p className="text-teal-400 text-xs font-bold uppercase tracking-widest mb-1">Step 2 of 5</p>
        <h2 className="text-white text-lg font-bold mb-1">Enter Your Activity Data</h2>
        <p className="text-slate-400 text-sm mb-5">
          Fuel usage, electricity, and gas throughput. We calculate the rest.
        </p>
        <button onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-500"
          data-testid="start-journey-btn">
          Start Your Journey <ArrowRight size={14} />
        </button>
      </div>

      {/* Steps tracker */}
      <SectionCard>

        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold text-slate-900">Your Climate Journey</p>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Step 1 of 5 complete</span>
        </div>
        <div className="space-y-2.5">
          {STEPS_EMPTY.map(s => (
            <div key={s.n} className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                s.done ? "bg-teal-500 text-white" : s.n === 2 ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-400"
              }`}>
                {s.done ? <CheckCircle2 size={12} /> : s.n}
              </div>
              <span className={`text-xs ${s.done ? "text-teal-600 font-semibold" : s.n === 2 ? "text-slate-900 font-bold" : "text-slate-400"}`}>
                {s.label}
              </span>
              {s.n === 2 && <span className="ml-auto text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Next</span>}
            </div>
          ))}
        </div>
      
      </SectionCard>
    </div>
  );
}

// ── SDG Impact Card ────────────────────────────────────────────────────────
function SDGImpactCard({ journey }) {
  const actions = journey?.actions?.actions || [];
  const RELEVANCE_COLOR = {
    High:   "text-emerald-700 bg-emerald-50",
    Medium: "text-amber-700 bg-amber-50",
    Low:    "text-slate-500 bg-slate-50",
  };
  const DRIVER_MAP = {
    solar_pv: "Solar energy installation", renewable_energy: "Renewable energy",
    efficiency: "Energy efficiency", energy_efficiency: "Energy efficiency",
    transport: "Fuel switch / transport", fuel_switch: "Fuel switch",
    waste_reduction: "Waste reduction", value_chain: "Value chain emissions",
  };

  // Aggregate unique SDGs
  const sdgMap = {};
  actions.forEach(a => {
    (a.sdg_tags || []).forEach(sdg => {
      if (!sdgMap[sdg.id]) {
        const atype = a.action_type || "";
        const relevance = ["value_chain","renewable_energy","solar_pv","transport","fuel_switch"].includes(atype) ? "High" : "Medium";
        sdgMap[sdg.id] = { ...sdg, relevance, driver: DRIVER_MAP[atype] || atype.replace(/_/g," ") };
      }
    });
  });
  const sdgs = Object.values(sdgMap).sort((a,b) => a.id - b.id);
  if (!sdgs.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="sdg-impact-card">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Sustainability Contribution (SDGs)</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs text-slate-400 uppercase pb-2 pr-3">SDG</th>
              <th className="text-left text-xs text-slate-400 uppercase pb-2 pr-3">Relevance</th>
              <th className="text-left text-xs text-slate-400 uppercase pb-2">Driver</th>
            </tr>
          </thead>
          <tbody className="space-y-1">
            {sdgs.map(sdg => (
              <tr key={sdg.id} className="border-b border-slate-50">
                <td className="py-1.5 pr-3">
                  <p className="font-bold text-slate-900 text-xs">{sdg.name}</p>
                  <p className="text-xs text-slate-400 leading-tight">{sdg.title}</p>
                </td>
                <td className="py-1.5 pr-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${RELEVANCE_COLOR[sdg.relevance] || RELEVANCE_COLOR.Medium}`}>
                    {sdg.relevance}
                  </span>
                </td>
                <td className="py-1.5 text-xs text-slate-500">{sdg.driver}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-2 italic">SDG alignment is indicative — based on your identified reduction actions.</p>
    </div>
  );
}


function TopActionsPreview({ journey, navigate }) {
  const actions = (journey?.actions?.actions || []).slice(0, 2);
  if (!actions.length) return null;

  const TYPE_COLOR = {
    renewable_energy: "bg-blue-50 text-blue-700",
    transport:        "bg-teal-50 text-teal-700",
    efficiency:       "bg-amber-50 text-amber-700",
    value_chain:      "bg-emerald-50 text-emerald-700",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="top-actions-preview">
      <div className="flex items-start justify-between mb-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Top Actions</p>
        <button onClick={() => navigate("/sme/journey")}
          className="text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
          Full plan <ChevronRight size={11} />
        </button>
      </div>
      <div className="space-y-3">
        {actions.map((a, i) => {
          const savings = a.annual_savings || 0;
          const shortDesc = a.description?.split(".")[0].replace(/\([^)]*\)/g, "").trim() ||
            a.action_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Target size={12} className="text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{shortDesc}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-teal-600 font-semibold">−{a.co2_reduction}t CO₂e/yr</span>
                  {savings > 0 && (
                    <span className="text-xs text-emerald-600 font-semibold">${savings.toLocaleString()}/yr saved</span>
                  )}
                </div>
              </div>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${TYPE_COLOR[a.action_type] || "bg-slate-100 text-slate-500"}`}>
                {a.action_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()).slice(0, 12)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="text-xs text-slate-400">
          Total savings potential: <span className="font-bold text-emerald-600">
            ${actions.reduce((s, a) => s + (a.annual_savings || 0), 0).toLocaleString()}/yr
          </span>
          {" · "}
          <span className="font-bold text-teal-600">
            {actions.reduce((s, a) => s + (a.co2_reduction || 0), 0).toFixed(1)}t CO₂e/yr
          </span> reduction
        </p>
      </div>
    </div>
  );
}


export default function SMEDashboard() {
  const navigate = useNavigate();
  const { user, currentOrg } = useAuth();
  const { journeyData } = useJourney();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const fp    = journeyData?.footprint;
  const opp   = journeyData?.opportunity;
  const act   = journeyData?.actions;
  const cars  = journeyData?.cars_score;
  const raw   = journeyData?.raw_inputs;
  const audit = journeyData?.audit_provenance;
  const peer  = journeyData?.peer_benchmark;
  const hasData = !!fp;

  if (!hasData) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <ActivationBanner />
        <EmptyJourneyDashboard user={user} org={currentOrg} navigate={navigate} />
      </div>
    );
  }

  // Energy cost estimate from footprint
  const DIESEL_PRICE = 0.46; // $/L approx Nigeria
  const GRID_TARIFF  = 0.07; // $/kWh approx Nigeria
  const fleetL   = raw?.fleet_diesel_liters     || 0;
  const genL     = raw?.generator_diesel_liters || 0;
  const elecKwh  = raw?.electricity_kwh         || 0;
  const energyCostEst = Math.round((fleetL + genL) * DIESEL_PRICE + elecKwh * GRID_TARIFF);

  // Last updated
  const lastUpdated = raw?.last_updated
    ? new Date(raw.last_updated).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : null;

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="sme-dashboard">
      {/* Show ActivationBanner only for incomplete journeys without data */}
      {user?.role !== "admin" && !fp && <ActivationBanner />}
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{currentOrg?.name}</p>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mt-0.5">{greeting}, {user?.first_name}</h1>
          {lastUpdated && <p className="text-xs text-slate-400 mt-0.5">Last updated {lastUpdated}</p>}
        </div>
        <button onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700"
          data-testid="update-data-btn">
          <Edit3 size={14} /> Update Data
        </button>
      </div>

      {/* ── Journey Progress Banner ── */}
      <div className="mb-5">
        <JourneyBanner journey={journeyData} navigate={navigate} />
      </div>

      {/* ── 4 KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KPICard
          icon={TrendingDown} iconBg="bg-slate-100" iconColor="text-slate-600"
          value={fp.total_tco2e} unit="tCO₂e"
          label="Carbon Footprint"
          sub="Per year"
        />
        <KPICard
          icon={Zap} iconBg="bg-amber-50" iconColor="text-amber-600"
          value={energyCostEst > 0 ? `$${(energyCostEst/1000).toFixed(0)}k` : null}
          label="Energy Cost Est."
          sub="Diesel + grid"
        />
        <KPICard
          icon={DollarSign} iconBg="bg-emerald-50" iconColor="text-emerald-600"
          value={opp?.vcm_value_low > 0
            ? (opp.vcm_value_low >= 1000
                ? `$${(opp.vcm_value_low/1000).toFixed(0)}k–$${(opp.vcm_value_high/1000).toFixed(0)}k`
                : `$${Math.round(opp.vcm_value_low)}–$${Math.round(opp.vcm_value_high)}`)
            : null}
          label="Carbon value (indicative)"
          sub="VCM $15–$30/tCO₂e range"
          tooltip="Based on voluntary carbon market price range of $15–$30/tCO₂e. Indicative only — actual value depends on project verification and market conditions."
        />
        <KPICard
          icon={Shield} iconBg="bg-blue-50" iconColor="text-blue-600"
          value={fp.confidence_classification ? fp.confidence_classification.charAt(0).toUpperCase() + fp.confidence_classification.slice(1) : null}
          label="Data Confidence"
          sub={cars?.score ? `CRS ${Math.round(cars.score)}/100 — Band ${cars.band || "C"}` : undefined}
        />
      </div>

      {/* ── Main 2-col section ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-2 space-y-4">
          <ClimateSnapshot journey={journeyData} navigate={navigate} />
          <TopActionsPreview journey={journeyData} navigate={navigate} />
        </div>
        <div className="space-y-4">
          <TopOpportunityCard journey={journeyData} navigate={navigate} />
          <NextActionCard journey={journeyData} navigate={navigate} />
          {/* SDG Impact */}
          <SDGImpactCard journey={journeyData} />
          {/* CRS Panel — show when data exists */}
          {cars && <CRSPanel carsData={cars} peerBenchmark={peer} />}
          {/* Audit Provenance Panel */}
          {audit && <AuditProvenancePanel auditData={audit} />}
          {/* Activation Banner — only show when journey is NOT yet complete */}
          {user?.role !== "admin" && fp?.total_tco2e && !(cars?.score >= 50) && <ActivationBanner />}
        </div>
      </div>

      {/* ── Footer CTAs ── */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-2 px-4 py-2.5 border border-teal-200 text-teal-700 text-sm font-semibold rounded-xl hover:bg-teal-50 bg-white"
          data-testid="footer-update-data">
          <Edit3 size={14} /> Update Data
        </button>
        <button onClick={() => navigate("/sme-reports")}
          className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 bg-white"
          data-testid="footer-view-report">
          <FileText size={14} /> View Reports
        </button>
        <button onClick={() => navigate("/sme/journey")}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800"
          data-testid="footer-request-financing">
          <DollarSign size={14} /> Request Financing
        </button>
      </div>

      {/* ── Unlock Financing progression ── */}
      <UnlockFinancingDashboard navigate={navigate} />
    </div>
  );
}
